import React, { useState, useEffect } from 'react';
import businessService, { Business } from '../../services/businessService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { getTimezonesByCountry } from '../../utils/timezones';

interface BusinessFormProps {
  open: boolean;
  business: Business | null;
  onClose: () => void;
  onSuccess: () => void;
}

const BusinessForm: React.FC<BusinessFormProps> = ({ open, business, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    address: '',
    phone: '',
    email: '',
    manager_name: '',
    timezone: 'UTC'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Resetear formulario cuando cambia el negocio o se abre/cierra
  useEffect(() => {
    if (open) {
      if (business) {
        setFormData({
          name: business.name || '',
          industry: business.industry || '',
          address: business.address || '',
          phone: business.phone || '',
          email: business.email || '',
          manager_name: business.manager_name || '',
          timezone: business.timezone || 'UTC'
        });
      } else {
        setFormData({
          name: '',
          industry: '',
          address: '',
          phone: '',
          email: '',
          manager_name: '',
          timezone: 'UTC'
        });
      }
      setError(null);
      setErrors({});
    }
  }, [open, business]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.nombre = 'El nombre es obligatorio';
    } else if (formData.name.length < 2) {
      newErrors.nombre = 'El nombre debe tener al menos 2 caracteres';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData.phone && formData.phone.length < 8) {
      newErrors.telefono = 'Teléfono inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpiar error del campo al editar
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (business) {
        await businessService.update(business.id, formData);
      } else {
        await businessService.create(formData);
      }

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar negocio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {business ? 'Editar Negocio' : 'Nuevo Negocio'}
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre del Negocio"
                name="name"
                value={formData.name || ''}
                onChange={handleChange}
                required
                error={!!errors.nombre}
                helperText={errors.nombre}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rubro"
                name="industry"
                value={formData.industry || ''}
                onChange={handleChange}
                disabled={loading}
                placeholder="Ej: Gastronomía, Retail, etc."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección"
                name="address"
                value={formData.address || ''}
                onChange={handleChange}
                disabled={loading}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                disabled={loading}
                error={!!errors.telefono}
                helperText={errors.telefono}
                placeholder="+54 11 1234-5678"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange}
                disabled={loading}
                error={!!errors.email}
                helperText={errors.email}
                placeholder="contacto@negocio.com"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre de Contacto"
                name="manager_name"
                value={formData.manager_name || ''}
                onChange={handleChange}
                disabled={loading}
                placeholder="Nombre del responsable"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth disabled={loading}>
                <InputLabel>Zona Horaria</InputLabel>
                <Select
                  label="Zona Horaria"
                  name="timezone"
                  value={formData.timezone || 'UTC'}
                  onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value as string }))}
                >
                  {Object.entries(getTimezonesByCountry()).map(([country, zones]) => [
                    <MenuItem key={`header-${country}`} disabled sx={{ fontWeight: 'bold', opacity: 1 }}>
                      {country}
                    </MenuItem>,
                    ...zones.map(tz => (
                      <MenuItem key={tz.value} value={tz.value} sx={{ pl: 3 }}>
                        {tz.label} ({tz.offset})
                      </MenuItem>
                    ))
                  ])}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} />}
          >
            {business ? 'Guardar Cambios' : 'Crear Negocio'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BusinessForm;
